import type { SyncProgress } from './progressSync';

export const IDENTITY_STORAGE_KEY = 'causal-atlas:identity:v1';

export interface StoredIdentity {
  code: string;
  verifiedAt: string;
}

export interface RemoteProgress {
  exists: boolean;
  version: number;
  state: SyncProgress | null;
  updatedAt: string | null;
}

export class SyncRequestError extends Error {
  status: number;
  code: string;

  constructor(message: string, status = 0, code = 'SYNC_REQUEST_FAILED') {
    super(message);
    this.name = 'SyncRequestError';
    this.status = status;
    this.code = code;
  }
}

export function readStoredIdentity(): StoredIdentity | null {
  try {
    const raw = localStorage.getItem(IDENTITY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredIdentity>;
    if (typeof parsed.code !== 'string' || !/^\d{6}$/.test(parsed.code)) return null;
    return { code: parsed.code, verifiedAt: typeof parsed.verifiedAt === 'string' ? parsed.verifiedAt : '' };
  } catch {
    return null;
  }
}

export function saveStoredIdentity(identity: StoredIdentity): void {
  localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(identity));
}

export function clearStoredIdentity(): void {
  localStorage.removeItem(IDENTITY_STORAGE_KEY);
}

async function request(path: string, options: RequestInit = {}): Promise<Response> {
  try {
    return await fetch(path, {
      ...options,
      cache: 'no-store',
      headers: {
        'content-type': 'application/json',
        ...(options.headers || {}),
      },
    });
  } catch {
    throw new SyncRequestError('同步服务暂不可达；本地数据未受影响。', 0, 'SYNC_OFFLINE');
  }
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  return (await response.json().catch(() => ({}))) as Record<string, unknown>;
}

export async function verifyPersonalCode(code: string): Promise<boolean> {
  const response = await request('/api/identity/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  const body = await readJson(response);
  if (response.status === 401) throw new SyncRequestError('验证码不正确。', 401, 'INVALID_CODE');
  if (response.status === 429) throw new SyncRequestError('尝试次数过多，请稍后再试。', 429, 'RATE_LIMITED');
  if (!response.ok) throw new SyncRequestError('验证服务暂时不可用。', response.status);
  return body.ok === true;
}

export async function fetchRemoteProgress(code: string): Promise<RemoteProgress> {
  const response = await request('/api/sync', {
    method: 'GET',
    headers: { 'x-personal-code': code },
  });
  const body = await readJson(response);
  if (response.status === 401) throw new SyncRequestError('验证码无效，请重新输入。', 401, 'INVALID_CODE');
  if (!response.ok) throw new SyncRequestError('读取云端进度失败；本地数据未受影响。', response.status);
  return {
    exists: body.exists === true,
    version: Number(body.version) || 0,
    state: (body.state ?? null) as SyncProgress | null,
    updatedAt: typeof body.updatedAt === 'string' ? body.updatedAt : null,
  };
}

export interface WriteResult {
  version: number;
  updatedAt: string;
}

export async function writeRemoteProgress(code: string, state: SyncProgress, baseVersion: number): Promise<WriteResult> {
  const response = await request('/api/sync', {
    method: 'PUT',
    headers: { 'x-personal-code': code },
    body: JSON.stringify({ state, baseVersion }),
  });
  const body = await readJson(response);
  if (response.status === 401) throw new SyncRequestError('验证码无效，请重新输入。', 401, 'INVALID_CODE');
  if (response.status === 409) {
    const conflict = new SyncRequestError('云端数据已被另一台设备更新。', 409, 'SYNC_CONFLICT');
    (conflict as SyncRequestError & { remote?: RemoteProgress }).remote = {
      exists: body.exists === true,
      version: Number(body.version) || 0,
      state: (body.state ?? null) as SyncProgress | null,
      updatedAt: typeof body.updatedAt === 'string' ? body.updatedAt : null,
    };
    throw conflict;
  }
  if (!response.ok) throw new SyncRequestError('写入云端进度失败；本地数据未受影响。', response.status);
  return {
    version: Number(body.version) || baseVersion + 1,
    updatedAt: typeof body.updatedAt === 'string' ? body.updatedAt : new Date().toISOString(),
  };
}
